import {
    Autocomplete,
    Checkbox,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import Highcharts from 'highcharts';
import { HighchartsReact } from 'highcharts-react-official';
import 'highcharts/highcharts-more';
import { useEffect, useMemo, useState } from 'react';
import { ENTITY_SPECS } from '../../models/EntitySpecs';
import { api } from '../../services/Server';
import { formatDecimal } from '../../utils/format';
import { CHART_COLORS, PLOT_OPTIONS } from './constants';
import { ComparableField, EntityRecord } from './types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const EMPTY_ARRAY: EntityRecord[] = [];

/** Try to extract a numeric value from a field (handles number and numeric strings). */
const toNumber = (v: unknown): number | null => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

/** Build a display label for an entity, prefixing supplier name for cell products. */
const getItemLabel = (
  item: EntityRecord,
  isCellProduct: boolean,
  supplierMap: Map<number, string>,
): string => {
  const name = (item.name as string) ?? `#${item.id}`;
  if (isCellProduct) {
    const sName = supplierMap.get(item.supplier_id as number);
    if (sName) return `${sName} - ${name}`;
  }
  return name;
};

// ---------------------------------------------------------------------------
// CompareTab component
// ---------------------------------------------------------------------------

interface CompareTabProps {
  entityKey: string;
  comparableFields: ComparableField[];
}

const CompareTab = ({ entityKey, comparableFields }: CompareTabProps) => {
  const spec = ENTITY_SPECS.find((s) => s.key === entityKey)!;
  const isCellProduct = entityKey === 'cell-products';

  const { data: items = EMPTY_ARRAY } = useQuery<EntityRecord[]>({
    queryKey: [spec.path],
    queryFn: () => api.list(spec.path),
  });

  const { data: suppliers = EMPTY_ARRAY } = useQuery<EntityRecord[]>({
    queryKey: ['suppliers'],
    queryFn: () => api.list('suppliers'),
    enabled: isCellProduct,
  });

  const supplierMap = useMemo(() => {
    const m = new Map<number, string>();
    suppliers.forEach((s) => m.set(s.id, (s.name as string) ?? ''));
    return m;
  }, [suppliers]);

  const [selectedItems, setSelectedItems] = useState<EntityRecord[]>([]);
  const [selectedFields, setSelectedFields] = useState<ComparableField[]>([]);
  const [chartOptions, setChartOptions] = useState<Highcharts.Options | null>(null);

  const selectedItemIds = selectedItems.map((i) => i.id).join(',');
  const selectedFieldNames = selectedFields.map((f) => f.name).join(',');

  const itemOptions = useMemo(
    () =>
      items.map((it) => ({
        ...it,
        _label: getItemLabel(it, isCellProduct, supplierMap),
      })).sort((a, b) => a._label.localeCompare(b._label)),
    [items, isCellProduct, supplierMap],
  );

  // Rebuild the chart whenever selections change
  useEffect(() => {
    if (selectedItems.length === 0 || selectedFields.length === 0) {
      setChartOptions(null);
      return;
    }

    let cancelled = false;

    (async () => {
      const fullItems = await Promise.all(
        selectedItems.map((item) => api.get<EntityRecord>(spec.path, item.id)),
      );
      if (cancelled) return;

      const categories = fullItems.map((item) => getItemLabel(item, isCellProduct, supplierMap));

      const series: Highcharts.SeriesOptionsType[] = selectedFields.flatMap<Highcharts.SeriesOptionsType>((f, fIdx) => {
        const color = CHART_COLORS[fIdx % CHART_COLORS.length];

        if (f.range) {
          // Dumbbell chart: a connecting bar + dot markers at min/max
          const rangeData = fullItems.map((item) => {
            const low = toNumber(item[f.range!.from]);
            const high = toNumber(item[f.range!.to]);
            return low !== null && high !== null ? [low, high] as [number, number] : [null, null] as [null, null];
          });
          return [
            // Connecting bar (semi-transparent, moderate width)
            {
              type: 'columnrange' as const,
              name: f.label,
              color: Highcharts.color(color).setOpacity(0.25).get(),
              borderColor: color,
              borderWidth: 1.5,
              pointWidth: 14,
              borderRadius: 7,
              data: rangeData.map(([low, high]) => ({ low: low ?? undefined, high: high ?? undefined })),
              enableMouseTracking: true,
              showInLegend: true,
            },
            // Min marker
            {
              type: 'scatter' as const,
              name: `${f.label} (min)`,
              color,
              marker: { radius: 7, symbol: 'circle' as const, lineWidth: 2, lineColor: '#fff' },
              data: rangeData.map(([low]) => low),
              enableMouseTracking: false,
              showInLegend: false,
            },
            // Max marker
            {
              type: 'scatter' as const,
              name: `${f.label} (max)`,
              color,
              marker: { radius: 7, symbol: 'circle' as const, lineWidth: 2, lineColor: '#fff' },
              data: rangeData.map(([, high]) => high),
              enableMouseTracking: false,
              showInLegend: false,
            },
          ];
        }

        return [{
          type: 'scatter' as const,
          name: f.label,
          color,
          marker: { radius: 6, symbol: 'circle' as const },
          data: fullItems.map((item) => toNumber(item[f.name])),
        }];
      });

      setChartOptions({
        chart: { style: { fontFamily: '"DM Sans", sans-serif' } },
        title: { text: `${spec.singular} Comparison`, style: { fontWeight: '700', color: '#002A5C', fontSize: '16px' } },
        xAxis: {
          categories,
          labels: { style: { color: '#5C6B7A', fontSize: '12px' } },
        },
        yAxis: {
          title: { text: 'Value', style: { color: '#5C6B7A' } },
          gridLineColor: '#EDF1F7',
          labels: { style: { color: '#5C6B7A', fontSize: '11px' } },
        },
        tooltip: {
          shared: false,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          formatter: function (this: any) {
            const p = this.point;
            const name = this.series.name;
            const category = this.point.category || this.x;
            if ('low' in p && 'high' in p && p.low != null && p.high != null) {
              return `<b>${category}</b><br/>\u25CF ${name}: ${formatDecimal(p.low)} \u2013 ${formatDecimal(p.high)}`;
            }
            return `<b>${category}</b><br/>\u25CF ${name}: ${formatDecimal(this.y ?? 0)}`;
          },
          backgroundColor: '#fff',
          borderColor: '#DEE3EA',
          borderRadius: 8,
          shadow: { color: 'rgba(0,42,92,0.08)', offsetX: 0, offsetY: 2, width: 8 },
          style: { fontSize: '12px', color: '#002A5C' },
        },
        legend: {
          align: 'center',
          verticalAlign: 'bottom',
          itemStyle: { color: '#002A5C', fontWeight: '500', fontSize: '12px' },
          itemHoverStyle: { color: '#009DE0' },
          symbolRadius: 4,
        },
        colors: CHART_COLORS,
        plotOptions: {
          columnrange: {
            grouping: false,
            shadow: false,
            dataLabels: {
              enabled: true,
              inside: false,
              style: { fontSize: '11px', fontWeight: '600', color: '#002A5C', textOutline: 'none' },
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              formatter: function (this: any) {
                return formatDecimal(this.y);
              },
            },
          },
          scatter: PLOT_OPTIONS as Highcharts.PlotScatterOptions,
        },
        series,
        credits: { enabled: false },
      });
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemIds, selectedFieldNames, spec.path, spec.singular, isCellProduct, suppliers]);

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ alignItems: { xs: 'stretch', md: 'flex-end' } }}
      >
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={itemOptions}
          getOptionLabel={(o) => o._label}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={selectedItems.map((si) => itemOptions.find((io) => io.id === si.id)!).filter(Boolean)}
          onChange={(_, v) => setSelectedItems(v)}
          renderOption={({ key, ...props }, option, { selected }) => (
            <li key={key} {...props}>
              <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
              {option._label}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label={`Select ${spec.label}`} placeholder={spec.label} size="small" />
          )}
          limitTags={2}
          sx={{ flex: 1, minWidth: 280 }}
        />

        <Autocomplete
          options={comparableFields}
          getOptionLabel={(o) => o.range ? `${o.label} (range)` : o.label}
          isOptionEqualToValue={(a, b) => a.name === b.name}
          value={selectedFields[0] ?? null}
          onChange={(_, v) => setSelectedFields(v ? [v] : [])}
          renderOption={({ key, ...props }, option) => (
            <li key={key} {...props}>
              {option.label}
              {option.range && (
                <Typography variant="caption" sx={{ ml: 1, color: '#5C6B7A' }}>(range)</Typography>
              )}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Select field" placeholder="Field" size="small" />
          )}
          sx={{ flex: 1, minWidth: 280 }}
        />
      </Stack>

      {chartOptions && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid #DEE3EA',
            p: { xs: 2, md: 3 },
          }}
        >
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </Paper>
      )}
    </Stack>
  );
};

export default CompareTab;
